<template>
    <div class="content container-fluid">
        <!-- Page Header -->
        <div class="page-header">
            <div class="row align-items-center">
                <div class="col">
                    <h3 class="page-title">My Car Park Claim</h3>
                </div>
            </div>
        </div>
        <!-- /Page Header -->

        <!-- Form -->
        <form>
            <!-- Filter -->
            <div class="row align-items-center">
                <div class="col-md-2">Employee Name/ID</div>
                <div class="col-md-4 pl-md-0">
                    <Select2 v-model="userId" :options="employees" v-if="tokenObj.Role == 'admin'" required
                        @change="onUserChanged" />
                    <span v-else>{{ employees.length ? employees[0].text : this.loadingName }}</span>
                </div>

                <div class="col-md-2 ml-md-4">Department</div>
                <div class="col-md-3 pl-md-0">
                    {{ department }}
                </div>
            </div>
            <div class="row align-items-center mt-2">
                <div class="col-md-2">Month</div>
                <div class="col-md-2 pl-md-0">
                    <select class="custom-select" v-model="month" @change="onMonthChanged($event)">
                        <option v-for="month in months" :key="month.value" :value="month.value">
                            {{ month.text }}
                        </option>
                    </select>
                </div>

                <div class="col-md-2 pl-md-0">&nbsp;</div>

                <div class="col-md-2 ml-md-4">Year</div>
                <div class="col-md-2 pl-md-0">
                    <select class="custom-select" v-model="year" @change="onYearChanged($event)">
                        <option value="">-- Please Select --</option>
                        <option v-for="year in years" :key="year" :value="year">
                            {{ year }}
                        </option>
                    </select>
                </div>
            </div>
            <div class="row align-items-center mt-3">
                <div class="col-md-6">
                    <button type="button" v-if="showSaveSubmit" class="btn btn-secondary" @click="saveAsDraft">
                        <i class="fa fa-save"></i> Save as Draft
                    </button>
                    <button type="button" v-if="showSaveSubmit" class="btn btn-primary" @click="submit">
                        <i class="fa fa-play"></i> Submit
                    </button>
                    <button v-if="tokenObj.Role == 'admin' && status == 'APPROVED'" type="button"
                        class="btn btn-primary" @click="changeToDraft">
                        <i class="fa fa-backward"></i> Change to Draft
                    </button>
                </div>

                <div class="col-md-2 ml-md-4">Status</div>
                <div class="col-md-2 pl-md-0">
                    {{ statusDisplay }}
                </div>
            </div>
            <!-- /Filter -->

            <!-- Table List -->
            <div class="row mt-4">
                <div class="col-md-12">
                    <div class="table-responsive mb-lg-0 mb-md-0 mb-sm-1">
                        <table class="table table-bordered custom-table mb-0" style="width: 900px">
                            <thead class="thead-light">
                                <tr>
                                    <th style="width:25%">Date</th>
                                    <th style="width:30%">Day</th>
                                    <th>Car Park Charges Incurred</th>
                                </tr>
                                <tr class="actual-amount-row">
                                    <th></th>
                                    <th>Actual Amount</th>
                                    <th class="total">{{ actualAmount }}</th>
                                </tr>
                                <tr class="payable-amount-row">
                                    <th></th>
                                    <th>Payable Amount</th>
                                    <th class="total">{{ payableAmount }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, i) in dates" :key="i">
                                    <td>{{ item }}</td>
                                    <td>{{ days[i] }}</td>
                                    <td class="cell-number">
                                        <input type="text" maxlength="10" class="form-control" placeholder="0.00"
                                            v-model="claimDetails[i].Amount" @keydown="allowNumberCommaDot($event)"
                                            @keyup="calculateTotalAmount(i)"
                                            v-if="showSaveSubmit || isChange || status == 'BLANK'" />
                                        <span v-else>{{ claimDetails[i].Amount }}</span>
                                    </td>
                                </tr>
                            </tbody>

                        </table>
                    </div>
                </div>
            </div>
            <!-- /Table List -->

            <!-- Attachment -->
            <div class="row mt-4">
                <div class="col-md-12">
                    <div class="mb-4" v-if="status == 'BLANK' || status == 'DRAFT' || status == 'REJECTED'">
                        <span v-if="attachmentURL == null || attachmentURL == ''">Attach File</span>
                        <span v-else>New Attach File</span>
                        <input type="file" v-on:change="setAttachment" ref="fileupload" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx" />

                        <p>
                            Allowed file extension(s): jpg, jpeg, png, pdf, doc, docx, xls, xlsx
                            <br>
                            Maximum file size per attachment: {{ maxUploadFileSize }}MB
                        </p>
                    </div>

                    <div v-if="attachmentURL != null && attachmentURL != ''">
                        Uploaded File:
                        <a :href="attachmentURL" target="_blank">{{ attachmentFilename }}</a>
                    </div>
                </div>
            </div>
            <!-- /Attachment -->

            <!-- Remarks -->
            <div class="row mt-4"
                v-if="showManagerButtons || status == 'APPROVED' || status == 'REJECTED' || status == 'PENDING'">
                <div class="col-md-12">
                    <span style="font-weight:500">
                        Remarks is mandatory if you are rejecting or changing this car park claims.
                    </span>
                    <textarea v-model="remarks" class="form-control" maxlength="2000" style="height:80px"
                        :disabled="status == 'APPROVED' || status == 'REJECTED' || (status == 'PENDING' && tokenObj.Role == 'employee' && !tokenObj.HasCarparkClaimDelegation)">
                    </textarea>
                </div>
            </div>
            <!-- /Remarks -->

            <!-- Buttons -->
            <div class="row align-items-center mt-3">
                <div class="col-md-12 text-center manager-button" v-if="!isChange">
                    <template v-if="showManagerButtons && !isChange">
                        <button type="button" class="btn btn-primary" @click="approve">
                            <i class="fa fa-play"></i> Approve
                        </button>
                        <button type="button" class="btn btn-danger ml-3" @click="reject">
                            <i class="fa fa-ban"></i> Reject
                        </button>
                        <button type="button" class="btn btn-secondary ml-3" @click="change">
                            <i class="fa fa-edit"></i> Change
                        </button>
                    </template>
                </div>
                <div class="col-md-12 text-center manager-button" v-if="isChange">
                    <button type="button" class="btn btn-primary" @click="saveChange">
                        <i class="fa fa-save"></i> Save
                    </button>
                    <button type="button" class="btn btn-secondary ml-3" @click="cancelChange">
                        <i class="fa fa-window-close-o"></i> Cancel
                    </button>
                </div>
            </div>
            <!-- Buttons -->
        </form>
        <!-- /Form -->
    </div>
</template>

<script>
import API from "../../http-common.js";
import config from "../../config.js";
export default {
    data() {
        return {
            userId: "",
            employees: [],
            department: "",
            months: [],
            month: "",
            years: [],
            year: "",
            status: "",
            statusDisplay: "",
            remarks: "",
            loadingName: config.LOADING_EMPLOYEE_NAME,
            actualAmount: "0.00",
            payableAmount: "0.00",
            payableAmountCap: 0,
            dates: [],
            days: [],
            claimDetails: [],
            //button show/hide
            isChange: false,
            showSaveSubmit: false,
            showManagerButtons: false,
            newAttachment: null,
            attachment: null,
            attachmentFilename: '',
            newAttachmentFilename: '',
            attachmentURL: '',
            maxUploadFileSize: config.UPLOAD_FILE_LIMIT_MB,
        }
    },

    computed: {
        tokenObj() {
            let data = JSON.parse(localStorage.getItem("lambitoken"));
            return data;
        },
        allMonths(){
            let months = [];
            months.push({ text: "Jan", value: 1 });
            months.push({ text: "Feb", value: 2 });
            months.push({ text: "Mar", value: 3 });
            months.push({ text: "Apr", value: 4 });
            months.push({ text: "May", value: 5 });
            months.push({ text: "Jun", value: 6 });
            months.push({ text: "Jul", value: 7 });
            months.push({ text: "Aug", value: 8 });
            months.push({ text: "Sep", value: 9 });
            months.push({ text: "Oct", value: 10 });
            months.push({ text: "Nov", value: 11 });
            months.push({ text: "Dec", value: 12 });
            return months;
        }
    },

    methods: {
        //filter
        adjustMonthsShown(year){
            if (year == new Date().getFullYear()) {
                let currentMonth = new Date().getMonth() + 1;
                this.months = this.allMonths.filter(month => month.value <= currentMonth);
                if (!this.month || this.month > currentMonth) {
                    this.month = currentMonth;
                }
            } 
            else {
                this.months = this.allMonths;
            }
            this.generateDatesAndDays(this.month, year);
        },
        onUserChanged() {
            this.generateDatesAndDays(this.month, this.year);
            this.getCarparkClaimFormData();
            this.isChange = false;
        },
        onMonthChanged(event) {
            let month = event.target.value;
            this.generateDatesAndDays(month, this.year);
            this.getCarparkClaimFormData();
            this.isChange = false;
        },
        onYearChanged(event) {
            let year = event.target.value;
            this.adjustMonthsShown(year);
            this.getCarparkClaimFormData();
            this.isChange = false;
        },
        //table related
        generateDatesAndDays(month, year) {
            let numberOfDays = new Date(
                year,
                month,
                0
            ).getDate();

            this.dates = [];
            this.days = [];
            let dateShortName;
            this.claimDetails = [];
            for (let index = 0; index < numberOfDays; index++) {
                let dateDisplay = (index + 1) + "/" + month + "/" + year;
                this.dates.push(dateDisplay);
                dateShortName = new Date(year, month - 1, index + 1);
                let dayDisplay = dateShortName.toLocaleString('en-us', { weekday: 'short' })
                this.days.push(dayDisplay);

                this.claimDetails.push({ Date: null, DateDisplay: dateDisplay, Day: dayDisplay, Amount: null });
            }
        },
        setAttachment(event) {
            // console.log(event.target.files[0]);
            if (!event.target.files || !event.target.files[0]) {
                this.newAttachment = null;
                this.newAttachmentFilename = '';
                return;
            }
            var fileSizeInMB = event.target.files[0].size / 1024 / 1024;
            // console.log ( fileSizeInMB + " " + config.UPLOAD_FILE_LIMIT_MB );
            if ( fileSizeInMB < config.UPLOAD_FILE_LIMIT_MB ) {
                const reader = new FileReader();
                reader.readAsDataURL(event.target.files[0]);
                reader.onload = (event) => {
                    this.newAttachment = event.target.result;
                    //if ( this.status == config.CARPARKCLAIM_STATUS_NEW ) this.attachment = event.target.result;
                }

                if ( this.status == config.CARPARKCLAIM_STATUS_NEW ) this.attachmentFilename = event.target.files[0].name;
                this.newAttachmentFilename = event.target.files[0].name;
                // console.log(this.attachmentFilename);
            }
            else {
                this.$swal('Attachment file size cannot be more than ' + config.UPLOAD_FILE_LIMIT_MB + ' MB', { icon: "error" });
                
                this.$refs.fileupload.value=null;
            }
        },
        getEmployees() {
            let param = { Token: this.tokenObj.Token };
            this.$store.state.isLoading = true;
            API.getEmployees(param)
                .then((response) => {
                    this.$store.state.isLoading = false;
                    if (response.data.success) {
                        this.employees = response.data.record;
                    } else {
                        this.$swal(response.data.message, { icon: "error" });
                    }
                })
                .catch((error) => {
                    this.$store.state.isLoading = false;
                    this.$swal(error.response.data.Message, { icon: "error" });
                });
        },
        getYears() {
            let date = new Date();
            this.year = date.getFullYear();
            let minYear = date.getFullYear() - 5;
            for (let year = date.getFullYear(); year > minYear; year--) {
                this.years.push(year);
            }
            this.adjustMonthsShown(this.year);
        },
        getCarparkClaimFormData() {
            if (this.tokenObj.Role == "admin" && !this.userId) return;
            let param = {
                Token: this.tokenObj.Token,
                UserId: this.tokenObj.Role == "admin" ? this.userId : this.tokenObj.Id,
                Month: this.month,
                Year: this.year
            };
            this.$store.state.isLoading = true;
            API.getCarparkClaimFormData(param)
                .then((response) => {
                    this.$store.state.isLoading = false;
                    if (response.data.success) {
                        let data = response.data.record;
                        this.status = data.Status;
                        this.statusDisplay = data.StatusDisplay;
                        this.month = data.Month;
                        this.year = data.Year;
                        this.department = data.Department;
                        this.generateDatesAndDays(data.Month, data.Year);
                        this.payableAmount = data.PayableAmount ? this.formatNumber(data.PayableAmount) : "0.00";
                        this.payableAmountCap = data.PayableAmountCap ? data.PayableAmountCap : 0
                        this.actualAmount = data.ActualAmount ? this.formatNumber(data.ActualAmount) : "0.00";
                        this.remarks = data.Remarks;
                        this.attachment = data.Attachment;
                        this.attachmentFilename = data.AttachmentFilename;
                        this.attachmentURL = data.AttachmentURL;
                        this.newAttachment = null;
                        this.newAttachmentFilename = '';
                        if (data.CarparkClaimDetails && data.CarparkClaimDetails.length) {
                            for (let rec of data.CarparkClaimDetails) {
                                let item = this.claimDetails.find(c => c.DateDisplay == rec.DateFormmated)
                                if (item) item.Amount = this.formatNumber(rec.Amount);
                            }
                        }
                        //show/hide action buttons
                        if (this.tokenObj.Role == "admin" || this.tokenObj.HasCarparkClaimDelegation) {
                            if (data.Status == config.CARPARKCLAIM_STATUS_PENDING) this.showManagerButtons = true;
                            else this.showManagerButtons = false;

                            if ((data.Status == config.CARPARKCLAIM_STATUS_BLANK || data.Status == config.CARPARKCLAIM_STATUS_DRAFT || data.Status == config.CARPARKCLAIM_STATUS_REJECTED)) this.showSaveSubmit = true;
                            else this.showSaveSubmit = false;
                        }
                        else {
                            if ((data.Status == config.CARPARKCLAIM_STATUS_BLANK || data.Status == config.CARPARKCLAIM_STATUS_DRAFT || data.Status == config.CARPARKCLAIM_STATUS_REJECTED) && this.tokenObj.Id == data.UserId) this.showSaveSubmit = true;
                            else this.showSaveSubmit = false;
                        }
                    } else {
                        this.$swal(response.data.message, { icon: "error" });
                    }
                })
                .catch((error) => {
                    console.log(error);
                    this.$store.state.isLoading = false;
                    this.$swal(error.response.data.Message, { icon: "error" });
                });
        },
        //action button
        saveAsDraft() {
            //validate zero actual amount
            if (this.actualAmount == "0.00") {
                alert("Actual amount cannot be zero.");
                return;
            }
            //validate invalid amount
            for (let item of this.claimDetails) {
                if (!this.isValidNumber(item.Amount)) {
                    alert(`[${item.DateDisplay}]: Invalid amount ${item.Amount}`);
                    return;
                }
                if (this.hasMoreThanTwoDecimalPlaces(item.Amount)) {
                    alert(`[${item.DateDisplay}]: Amount ${item.Amount} has more than 2 decimal places.`);
                    return;
                }
            }

            let param = {
                Token: this.tokenObj.Token,
                UserId: this.tokenObj.Role == "admin" ? this.userId : this.tokenObj.Id,
                Month: this.month,
                Year: this.year,
                CarparkClaimDetails: this.claimDetails,
                Attachment: this.newAttachment,
                AttachmentFilename: this.newAttachmentFilename
            };
            this.$swal({
                title: "Are you sure?",
                text: "Are you sure you want to save the car park claim?",
                icon: "info",
                buttons: true,
                dangerMode: false,
            }).then((willSave) => {
                if (willSave) {
                    this.$store.state.isLoading = true;
                    API.saveCarparkClaimFormAsDraft(param)
                        .then((response) => {
                            this.$store.state.isLoading = false;
                            this.$swal(response.data.message, {
                                icon: response.data.success ? "success" : "error",
                            });
                            if (response.data.success) {
                                this.getCarparkClaimFormData();

                                this.$refs.fileupload.value=null;
                            }
                        })
                        .catch((error) => {
                            this.$store.state.isLoading = false;
                            this.$swal(error.response.data.Message, { icon: "error" });
                        });
                }
            });
        },
        submit() {
            if ( this.tokenObj.Role != "admin" && !this.tokenObj.HasManager ) {
                alert("No manager assigned.");
                return;
            }

            //validate zero actual amount
            if (this.actualAmount == "0.00") {
                alert("Actual amount cannot be zero.");
                return;
            }
            //validate invalid amount
            for (let item of this.claimDetails) {
                if (!this.isValidNumber(item.Amount)) {
                    alert(`[${item.DateDisplay}]: Invalid amount ${item.Amount}`);
                    return;
                }
                if (this.hasMoreThanTwoDecimalPlaces(item.Amount)) {
                    alert(`[${item.DateDisplay}]: Amount ${item.Amount} has more than 2 decimal places.`);
                    return;
                }
            }

            if ((this.attachment == "" || this.attachment == null) && (this.newAttachment == "" || this.newAttachment == null)) {
                alert("Please upload an attachment.");
                return;
            }

            let param = {
                Token: this.tokenObj.Token,
                UserId: this.tokenObj.Role == "admin" ? this.userId : this.tokenObj.Id,
                Month: this.month,
                Year: this.year,
                CarparkClaimDetails: this.claimDetails,
                Attachment: this.newAttachment,
                AttachmentFilename: this.newAttachmentFilename
            };
            this.$swal({
                title: "Are you sure?",
                text: "You can only submit one claim per month. Please check your claim before submitting. If there is an error/omission, you will be required to re-enter the entire month's carpark claims.",
                icon: "info",
                buttons: true,
                dangerMode: false,
            }).then((willSave) => {
                if (willSave) {
                    this.$store.state.isLoading = true;
                    API.submitCarparkClaimForm(param)
                        .then((response) => {
                            this.$store.state.isLoading = false;
                            this.$swal(response.data.message, {
                                icon: response.data.success ? "success" : "error",
                            });
                            if (response.data.success) {
                                this.getCarparkClaimFormData();

                                this.$refs.fileupload.value=null;
                            }
                        })
                        .catch((error) => {
                            this.$store.state.isLoading = false;
                            this.$swal(error.response.data.Message, { icon: "error" });
                        });
                }
            });
        },
        approve() {
            let param = {
                Token: this.tokenObj.Token,
                Remarks: this.remarks,
                UserId: this.tokenObj.Role == "admin" ? this.userId : this.tokenObj.Id,
                Month: this.month,
                Year: this.year,
            }
            this.$swal({
                title: "Are you sure?",
                text: "You are going to approve this car park claim. Click OK to continue.",
                icon: "info",
                buttons: true,
                dangerMode: false,
            }).then((willSave) => {
                if (willSave) {
                    this.$store.state.isLoading = true;
                    API.approveCarparkClaim(param)
                        .then((response) => {
                            this.$store.state.isLoading = false;
                            this.$swal(response.data.message, {
                                icon: response.data.success ? "success" : "error",
                            });
                            if (response.data.success) {
                                this.getCarparkClaimFormData();
                            }
                        })
                        .catch((error) => {
                            this.$store.state.isLoading = false;
                            this.$swal(error.response.data.Message, { icon: "error" });
                        });
                }
            });
        },
        reject() {
            //validate remarks
            if (!this.remarks) {
                alert("Please specify the reason why you are rejecting the timesheet.");
                return;
            }
            let param = {
                Token: this.tokenObj.Token,
                Remarks: this.remarks,
                UserId: this.tokenObj.Role == "admin" ? this.userId : this.tokenObj.Id,
                Month: this.month,
                Year: this.year,
            }
            this.$swal({
                title: "Are you sure?",
                text: "You are going to reject this car park claim. Click OK to continue.",
                icon: "info",
                buttons: true,
                dangerMode: false,
            }).then((willSave) => {
                if (willSave) {
                    this.$store.state.isLoading = true;
                    API.rejectCarparkClaim(param)
                        .then((response) => {
                            this.$store.state.isLoading = false;
                            this.$swal(response.data.message, {
                                icon: response.data.success ? "success" : "error",
                            });
                            if (response.data.success) {
                                this.getCarparkClaimFormData();
                            }
                        })
                        .catch((error) => {
                            this.$store.state.isLoading = false;
                            this.$swal(error.response.data.Message, { icon: "error" });
                        });
                }
            });
        },
        change() {
            this.isChange = true;
        },
        back() {
            this.$router.push({ name: "carparkClaimView" });
        },
        saveChange() {
            //validate remarks
            if (!this.remarks) {
                alert("Please indicate the reason for the changes.");
                return;
            }
            let param = {
                Token: this.tokenObj.Token,
                Remarks: this.remarks,
                UserId: this.tokenObj.Role == "admin" ? this.userId : this.tokenObj.Id,
                Month: this.month,
                Year: this.year,
                CarparkClaimDetails: this.claimDetails
            }
            this.$swal({
                title: "Are you sure?",
                text: "Are you sure you want to save the changes on the car park claim?",
                icon: "info",
                buttons: true,
                dangerMode: false,
            }).then((willSave) => {
                if (willSave) {
                    this.$store.state.isLoading = true;
                    API.saveChangeCarparkClaim(param)
                        .then((response) => {
                            this.$store.state.isLoading = false;
                            this.$swal(response.data.message, {
                                icon: response.data.success ? "success" : "error",
                            });
                            if (response.data.success) {
                                this.getCarparkClaimFormData();
                                this.isChange = false;
                            }
                        })
                        .catch((error) => {
                            this.$store.state.isLoading = false;
                            this.$swal(error.response.data.Message, { icon: "error" });
                        });
                }
            });
        },
        cancelChange() {
            this.isChange = false;
            this.getCarparkClaimFormData();
        },
        changeToDraft() {
            let param = {
                Token: this.tokenObj.Token,
                UserId: this.tokenObj.Role == "admin" ? this.userId : this.tokenObj.Id,
                Month: this.month,
                Year: this.year,
            }
            this.$swal({
                title: "Are you sure?",
                text: "Are you sure you want to change the car park claim status to draft?",
                icon: "info",
                buttons: true,
                dangerMode: false,
            }).then((willSave) => {
                if (willSave) {
                    this.$store.state.isLoading = true;
                    API.changeCarparkClaimToDraft(param)
                        .then((response) => {
                            this.$store.state.isLoading = false;
                            this.$swal(response.data.message, {
                                icon: response.data.success ? "success" : "error",
                            });
                            if (response.data.success) {
                                this.getCarparkClaimFormData();
                            }
                        })
                        .catch((error) => {
                            this.$store.state.isLoading = false;
                            this.$swal(error.response.data.Message, { icon: "error" });
                        });
                }
            });
        },
        //helper
        formatNumber(number) { //return 2 decimal place and add thousand separator
            return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number.toFixed(2));
        },
        allowNumberCommaDot(event) {
            var theEvent = event || window.event;
            var key = theEvent.keyCode || theEvent.which;
            var keyCode = key;
            key = String.fromCharCode(key);
            if (key.length == 0) return;
            var regex = /^[0-9.,\b]+$/;
            if (keyCode == 188 || keyCode == 190 || keyCode == 37 || keyCode == 39) {
                return;
            } else {
                if (!regex.test(key)) {
                    theEvent.returnValue = false;
                    if (theEvent.preventDefault) theEvent.preventDefault();
                }
            }
        },
        calculateTotalAmount(i) {
            //prevent occurence of dot more than once
            let countDot = 0
            let amount = this.claimDetails[i].Amount;
            let amountLength = amount ? amount.length : 0;
            if (amountLength > 0) {
                for (let i = 0; i < amountLength; i++) {
                    let character = amount.charAt(i);
                    if (character == ".") countDot++
                }
            }
            if (countDot > 1) {
                let lastIndex = amount.lastIndexOf(".");
                this.claimDetails[i].Amount = amount.substring(0, lastIndex) + amount.substring(lastIndex + 1);
            }
            //calculate sum
            let sum = 0;
            for (let item of this.claimDetails) {
                let amount = parseFloat(item.Amount ? item.Amount.replace(/,/g, '') : "0");
                sum += amount;
            }
            this.actualAmount = this.formatNumber(sum);
            this.payableAmount = sum > this.payableAmountCap ? this.formatNumber(this.payableAmountCap) : this.formatNumber(sum);
        },
        isValidNumber(number) {
            if (!number) return true;
            let numberArr = number.split(",");
            if (numberArr.length > 1) {
                if (numberArr[0].includes(".")) return false;

                let lastNumberPortion = numberArr[numberArr.length - 1];
                if (lastNumberPortion.includes(".")) {
                    let lastNumberPortionArr = lastNumberPortion.split(".");
                    if (lastNumberPortionArr[0].length != 3) return false;
                } else {
                    if (lastNumberPortion.length != 3) return false;
                }

                for (let i = numberArr.length - 2; i > 0; i--) {
                    if (numberArr[i].includes(".")) return false;
                    if (numberArr[i].length != 3) return false;
                }
            }
            return true;
        },
        hasMoreThanTwoDecimalPlaces(number) {
            if (!number) return false;
            let numberArr = number.split(".");
            if (numberArr.length > 1) {
                if (numberArr[1].length > 2) return true;
            }
            return false;
        }
    },

    created() {
        this.getEmployees();
        this.getYears();
        if (this.tokenObj.Role == "employee") this.getCarparkClaimFormData();
    }
}
</script>

<style scoped>
.total {
    font-weight: normal;
}

table th,
table td {
    text-align: center;
    padding: 0;
    outline: 1px solid #dee2e6;
    border: none;
    outline-offset: -1px;
}

.table-responsive {
    max-height: 610px;
    max-width: 917px;
}

thead th {
    top: 0;
    position: sticky;
    position: -webkit-sticky;
    z-index: 20
}

.actual-amount-row th,
.payable-amount-row th {
    position: sticky;
    position: -webkit-sticky;
    z-index: 2
}

.actual-amount-row th {
    top: 40px;
}

.payable-amount-row th {
    top: 80px;
}

.table-responsive .cell-number {
    padding: 10px 10px !important
}

.form-control {
    height: 35px;
}

.manager-button button {
    width: 150px;
}
</style>


// WEBPACK FOOTER //
// src/components/Carpark/MyCarParkClaim.vue